import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAnalyticsSummary } from '../../redux/actions/analyticsAction';
import FYSelector from '../../components/utility/FYSelector';
import Loader from '../../utility/Loader';

const AnalyticsDashboard = () => {
    const dispatch = useDispatch();
    const { summary, loading, startDate, endDate, financialYear } = useSelector(state => state.analytics);

    useEffect(() => {
        dispatch(getAnalyticsSummary(startDate, endDate));
    }, [dispatch, financialYear]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className="py-5">
            <div className="container">
                {/* Header */}
                <div className="mb-3">
                    <h2 className="text-primary fw-bold mb-1">Analytics</h2>
                    <p className="text-muted mb-0">All data is from <strong>Tax Invoice {financialYear.slice(0,2)}-{financialYear.slice(2)}</strong></p>
                </div>

                <FYSelector />

                {/* Date Range */}
                <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '12px' }}>
                    <div className="row g-3 align-items-end">
                        <div className="col-auto">
                            <label className="form-label small text-muted fw-semibold mb-1">START DATE</label>
                            <input type="date" className="form-control" value={startDate} onChange={e => dispatch({ type: 'SET_ANALYTICS_DATE_RANGE', payload: { startDate: e.target.value, endDate } })} />
                        </div>
                        <div className="col-auto">
                            <label className="form-label small text-muted fw-semibold mb-1">END DATE</label>
                            <input type="date" className="form-control" value={endDate} onChange={e => dispatch({ type: 'SET_ANALYTICS_DATE_RANGE', payload: { startDate, endDate: e.target.value } })} />
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary btn-sm" onClick={() => dispatch(getAnalyticsSummary(startDate, endDate))}>Apply</button>
                        </div>
                    </div>
                </div>

                {loading ? <Loader /> : (
                    <>
                        {/* Summary Row */}
                        {summary && (
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="card border-0 bg-primary text-white shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="card-body">
                                            <p className="small mb-1 opacity-75">Total Sales</p>
                                            <h4 className="fw-bold mb-0">₹{summary.totalSales?.toLocaleString('en-IN') || 0}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 bg-success text-white shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="card-body">
                                            <p className="small mb-1 opacity-75">Total Bills</p>
                                            <h4 className="fw-bold mb-0">{summary.totalBills || 0}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 bg-warning text-white shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="card-body">
                                            <p className="small mb-1 opacity-75">Unique Customers</p>
                                            <h4 className="fw-bold mb-0">{summary.totalCustomers || 0}</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Analytics Cards */}
                        <div className="row g-4">
                            {/* Top Products Card */}
                            <div className="col-md-4">
                                <Link to="/analytics/top-products" className="text-decoration-none">
                                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(13,110,253,0.15)'; }} onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                                        <div className="card-body p-4">
                                            <div className="mb-3" style={{ fontSize: '2.5rem' }}>📦</div>
                                            <h5 className="fw-bold text-primary">Top Products</h5>
                                            <p className="text-muted small mb-3">See which products have the highest sales volume and revenue in the selected period.</p>
                                            <span className="btn btn-primary btn-sm">View Statistics →</span>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Top Customers Card */}
                            <div className="col-md-4">
                                <Link to="/analytics/top-customers" className="text-decoration-none">
                                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(25,135,84,0.15)'; }} onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                                        <div className="card-body p-4">
                                            <div className="mb-3" style={{ fontSize: '2.5rem' }}>👥</div>
                                            <h5 className="fw-bold text-success">Top Customers</h5>
                                            <p className="text-muted small mb-3">Identify your most valuable customers by total orders and spending.</p>
                                            <span className="btn btn-success btn-sm">View Statistics →</span>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            {/* Sales Card */}
                            <div className="col-md-4">
                                <Link to="/analytics/sales" className="text-decoration-none">
                                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(255,193,7,0.2)'; }} onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                                        <div className="card-body p-4">
                                            <div className="mb-3" style={{ fontSize: '2.5rem' }}>📈</div>
                                            <h5 className="fw-bold text-warning">Sales</h5>
                                            <p className="text-muted small mb-3">Visualise your daily sales trend with an interactive chart.</p>
                                            <span className="btn btn-warning btn-sm text-white">View Statistics →</span>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default AnalyticsDashboard;
